// Epoch 17 & 28: Lead Response Detail Component with Threading
// Displays individual lead response with conversation parsing, reply request, and threading functionality
// Date: July 25, 2025 (Updated for Epoch 28, Enhanced for Epoch 34)

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../supabase/client';
import { useAuthSession } from '../../hooks/useAuthSession';
import { 
  LeadResponse, 
  ReplyRequest, 
  ConversationMessage,
  getLeadResponseActions,
  UserReplyRequestForm
} from '../../types/lead-responses';
// EPOCH 29: Removed modal imports - using navigation instead
// import AddFollowupButton from './AddFollowupButton';
// import FollowupModal from './FollowupModal';
// import { MessageDisplay } from './SharedMessageDisplay';

// Epoch 34: Import new components for ownership display
import StatusBadge from './components/StatusBadge';
import LeadProfile from './components/LeadProfile';
import OwnershipInfo from './components/OwnershipInfo';

interface OutreachTeamMember {
  id: string;
  team_member_name: string;
  is_active: boolean;
  role_description?: string;
  contact_email?: string;
  team_lead_id?: string;
  linkedin_url?: string;
  created_at: string;
  updated_at: string;
  aliases: OutreachTeamAlias[];
}

interface OutreachTeamAlias {
  id: string;
  team_config_id: string;
  alias_name: string;
  confidence_weight: number;
  is_primary: boolean;
  created_at: string;
}

interface LeadResponseDetailProps {
  leadResponseId: string;
  onClose?: () => void;
  onReplySuccess?: () => void;
}

const LeadResponseDetail: React.FC<LeadResponseDetailProps> = ({
  leadResponseId,
  onClose,
  onReplySuccess
}) => {
  const navigate = useNavigate();
  const { session, userRole } = useAuthSession();
  const user = session?.user;
  const isCSR = userRole === 'csr';
  console.log('🔍 LeadResponseDetail - userRole:', userRole, 'isCSR:', isCSR);
  const [leadResponse, setLeadResponse] = useState<LeadResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showReplyForm, setShowReplyForm] = useState(false);
  const [replyLoading, setReplyLoading] = useState(false);
  const [sequenceContinuationLoading, setSequenceContinuationLoading] = useState(false);
  const [sequenceContinuationSuccess, setSequenceContinuationSuccess] = useState(false);
  const [replyContent, setReplyContent] = useState('');
  const [userNotes, setUserNotes] = useState('');
  const [outreachTeamMembers, setOutreachTeamMembers] = useState<OutreachTeamMember[]>([]);
  // EPOCH 70: Disposition state
  const [dispositionSaving, setDispositionSaving] = useState(false);
  // Epoch 29: Removed modal state - using navigation instead
  // const [showFollowupModal, setShowFollowupModal] = useState(false);
  const [messagingConfig, setMessagingConfig] = useState<{
    max_messages_per_conversation: number;
    warning_threshold: number;
    auto_close_after_days: number;
    enable_ai_scoring: boolean;
  } | null>(null);
  // AI analysis functionality commented out for ESLint compliance
  // const [aiAnalysisData, setAiAnalysisData] = useState<ConversationMessage[] | null>(null);

  // const loadAIAnalysisData = useCallback(async (leadName: string, conversationRaw: string) => {
  //   try {
  //     console.log('🤖 Loading AI analysis data for:', leadName);
  //     
  //     // Query the AI analysis table for this lead's conversation
  //     const { data, error } = await supabase
  //       .from('ai_conversation_analysis')
  //       .select('parsed_conversation, ai_confidence_score, engagement_score, sentiment_score, intent_classification')
  //       .eq('lead_name', leadName)
  //       .order('created_at', { ascending: false })
  //       .limit(1);

  //     if (error) {
  //       console.warn('Failed to load AI analysis data:', error);
  //       return null;
  //     }

  //     if (data && data.length > 0) {
  //       console.log('✅ Found AI analysis data:', data[0]);
  //       return data[0].parsed_conversation;
  //     } else {
  //       console.log('💭 No AI analysis data found, will use rule-based parsing');
  //       return null;
  //     }
  //   } catch (error) {
  //     console.warn('❌ Error loading AI analysis data:', error);
  //     return null;
  //   }
  // }, []);

  // Epoch 28: Load messaging configuration
  const loadMessagingConfig = useCallback(async (companyId: string) => {
    try {
      const { data, error } = await supabase
        .from('company_messaging_config')
        .select('*')
        .eq('company_id', companyId)
        .maybeSingle(); // Use maybeSingle() instead of single() to handle no rows gracefully

      if (error || !data) {
        if (error) {
          console.warn('Error loading messaging config:', error);
        } else {
          console.log('No messaging config found for company, using defaults');
        }
        // Default configuration
        setMessagingConfig({
          max_message_exchanges: 5,
          warning_at_message_count: 8,
          auto_close_after_days: 7
        });
      } else {
        console.log('Loaded messaging config:', data);
        setMessagingConfig(data);
      }
    } catch (error) {
      console.error('Error loading messaging config:', error);
      // Default configuration on error
      setMessagingConfig({
        max_message_exchanges: 5,
        warning_at_message_count: 8,
        auto_close_after_days: 7
      });
    }
  }, []);

  const loadOutreachTeamMembers = useCallback(async () => {
    try {
      console.log('🔄 Loading outreach team members...');
      const { data, error } = await supabase.functions.invoke('outreach-team-config', {
        body: { action: 'list' }
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      console.log('✅ Successfully loaded outreach team:', data.data);
      setOutreachTeamMembers(data.data || []);
    } catch (error) {
      console.warn('❌ Failed to load outreach team members, using fallback:', error);
      // Use fallback team members if API fails
      console.log('⚠️ Using fallback team members');
      setOutreachTeamMembers([
        {
          id: 'fallback-1',
          team_member_name: 'Phoebe',
          is_active: true,
          aliases: [
            { alias_name: 'Phoebe', is_primary: true, confidence_weight: 1.0 },
            { alias_name: 'Phoebe Smith', is_primary: false, confidence_weight: 0.9 }
          ]
        },
        {
          id: 'fallback-2',
          team_member_name: 'Lilly',
          is_active: true,
          aliases: [
            { alias_name: 'Lilly', is_primary: true, confidence_weight: 1.0 },
            { alias_name: 'Lily', is_primary: false, confidence_weight: 0.8 }
          ]
        },
        {
          id: 'fallback-3',
          team_member_name: 'JD',
          is_active: true,
          aliases: [
            { alias_name: 'JD', is_primary: true, confidence_weight: 1.0 },
            { alias_name: 'J.D.', is_primary: false, confidence_weight: 0.9 }
          ]
        }
      ]);
    }
  }, []);

  const loadLeadResponse = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const { data, error } = await supabase.functions.invoke('lead-responses', {
        body: {
          action: 'get',
          id: leadResponseId
        }
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      setLeadResponse(data.data);
      
      // Epoch 28: Load messaging config for company
      if (data.data?.company_id) {
        await loadMessagingConfig(data.data.company_id);
      }
    } catch (error) {
      console.error('Error loading lead response:', error);
      setError(error.message || 'Failed to load lead response');
    } finally {
      setLoading(false);
    }
  }, [leadResponseId, loadMessagingConfig]);

  const markAsRead = useCallback(async () => {
    try {
      const { data, error } = await supabase.functions.invoke('lead-responses', {
        body: {
          action: 'update',
          id: leadResponseId,
          data: { status: 'read' }
        }
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      setLeadResponse(prev => prev ? { ...prev, status: 'read', read_by_user_at: new Date().toISOString() } : null);
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  }, [leadResponseId]);

  // useEffect hooks after callback definitions to avoid initialization issues
  useEffect(() => {
    // Load outreach team members first, then lead response
    const loadData = async () => {
      await loadOutreachTeamMembers();
      await loadLeadResponse();
    };
    loadData();
  }, [leadResponseId, loadLeadResponse, loadOutreachTeamMembers]);

  useEffect(() => {
    if (leadResponse && leadResponse.status === 'new') {
      markAsRead();
    }
  }, [leadResponse, markAsRead]);

  // EPOCH 29: Navigate to form for follow-up
  const handleAddFollowup = useCallback(() => {
    if (leadResponse) {
      // Navigate with both state and query params
      navigate(`/leads-inbox/add?mode=followup&parent=${leadResponse.id}`, {
        state: { 
          parentResponse: leadResponse 
        }
      });
    }
  }, [leadResponse, navigate]);

  const handleReplyRequest = async () => {
    if (!replyContent.trim()) return;

    setReplyLoading(true);
    try {
      const requestData: UserReplyRequestForm = {
        lead_response_id: leadResponseId,
        reply_content: replyContent.trim(),
        user_notes: userNotes.trim(),
        template_used: ''
      };

      const { data, error } = await supabase.functions.invoke('reply-requests', {
        body: {
          action: 'create',
          data: requestData
        }
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      // Refresh the lead response to show updated status and reply requests
      await loadLeadResponse();
      
      // Reset form
      setReplyContent('');
      setUserNotes('');
      setShowReplyForm(false);
      
      onReplySuccess?.();
    } catch (error) {
      console.error('Error creating reply request:', error);
      setError(error.message || 'Failed to create reply request');
    } finally {
      setReplyLoading(false);
    }
  };

  const handleSequenceContinuation = async () => {
    setSequenceContinuationLoading(true);
    setError(null);
    try {
      const requestData = {
        lead_response_id: leadResponseId,
        reply_content: 'Continue message sequence',
        user_notes: '',
        template_used: '',
        is_sequence_continuation: true
      };

      const { data, error } = await supabase.functions.invoke('reply-requests', {
        body: {
          action: 'create',
          data: requestData
        }
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      setSequenceContinuationSuccess(true);
      await loadLeadResponse();
      onReplySuccess?.();
    } catch (error) {
      console.error('Error creating sequence continuation request:', error);
      setError(error.message || 'Failed to request sequence continuation');
    } finally {
      setSequenceContinuationLoading(false);
    }
  };

  const handleMarkReplySent = async (replyRequestId: string) => {
    const actualMessage = prompt('Please enter the actual message you sent (or leave blank if same as requested):');
    const csrNotes = prompt('Any notes about this reply? (optional)');
    
    try {
      // Build the data object with only non-empty values
      const updateData: {
        sent_at: string;
        actual_message_sent?: string;
        csr_notes?: string;
      } = {
        sent_at: new Date().toISOString()
      };
      
      if (actualMessage && actualMessage.trim()) {
        updateData.actual_message_sent = actualMessage.trim();
      }
      
      if (csrNotes && csrNotes.trim()) {
        updateData.csr_notes = csrNotes.trim();
      }
      
      const { data, error } = await supabase.functions.invoke('reply-requests', {
        body: {
          action: 'mark_sent',
          id: replyRequestId,
          data: updateData
        }
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      // Refresh the lead response to show updated reply request
      await loadLeadResponse();
      
      // Show success message
      alert('Reply marked as sent successfully!');
    } catch (error) {
      console.error('Error marking reply as sent:', error);
      alert('Failed to mark reply as sent. Please try again.');
    }
  };

  const handleDispositionChange = async (newDisposition: string) => {
    if (!leadResponse) return;
    setDispositionSaving(true);
    try {
      const { data, error } = await supabase.functions.invoke('lead-responses', {
        body: {
          action: 'update',
          id: leadResponseId,
          data: { pipeline_stage: newDisposition || 'Lead Responded' }
        }
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      setLeadResponse(prev => prev ? {
        ...prev,
        pipeline_stage: (newDisposition || 'Lead Responded') as any,
        pipeline_stage_updated_by: user?.id,
        pipeline_stage_updated_at: new Date().toISOString()
      } : null);
    } catch (err) {
      console.error('Error updating disposition:', err);
      setError('Failed to update disposition');
    } finally {
      setDispositionSaving(false);
    }
  };

  // LinkedIn conversation parsing function - robust format handling
  const parseRawConversation = (rawConversation: string): ConversationMessage[] => {
    if (!rawConversation) return [];
    
    console.log('🔍 Parsing conversation with outreach team members:', outreachTeamMembers);
    
    const lines = rawConversation.split('\n').map(line => line.trim()).filter(line => line);
    const messages: ConversationMessage[] = [];
    
    // Robust parsing to handle diverse LinkedIn conversation formats
    // Supports multiple languages, date formats, and sender patterns
    
    let currentBlock: string[] = [];
    let currentDate = '';
    let currentTime = '';
    
    // Helper function to detect if a line is a date
    const isDateLine = (line: string): boolean => {
      // Pattern 1: "Wed Jan 22 2025" or "Thu Jan 23 2025"
      if (line.match(/^(Mon|Tue|Wed|Thu|Fri|Sat|Sun)?\s*\w{3}\s+\w{3}\s+\d{1,2}\s+\d{4}/i)) {
        return true;
      }
      // Pattern 2: "// Tue Jun 10 2025" (with comment prefix)
      if (line.match(/^\/\/\s*(Mon|Tue|Wed|Thu|Fri|Sat|Sun)?\s*\w{3}\s+\w{3}\s+\d{1,2}\s+\d{4}/i)) {
        return true;
      }
      return false;
    };
    
    // Helper function to detect if a line is a time
    const isTimeLine = (line: string): boolean => {
      return line.match(/^\d{1,2}:\d{2}\s*(am|pm|AM|PM)$/) !== null;
    };
    
    // Helper function to detect if a line is a person's name (potential sender)
    // Enhanced with multi-language support for Chinese + English names
    const detectSenderName = (line: string): string | null => {
      // Skip if line contains URL, email, or special formatting
      if (line.includes('http') || line.includes('@') || line.includes('www.') || line.includes('.com')) {
        return null;
      }
      
      // Enhanced pattern for names - supports Chinese characters, English names, and mixed formats
      // Examples: "王明", "Zhang Wei", "李小明", "John Smith", "王 Wei", "Adrian Wong", "张三丰"
      
      // Chinese name patterns (2-4 Chinese characters)
      const chineseNamePattern = /^([\u4e00-\u9fff]{2,4})$/;
      
      // Mixed Chinese-English names (Chinese + English or vice versa)
      const mixedNamePattern = /^([\u4e00-\u9fff]{1,3}\s+[A-Z][A-Za-z]{1,15}|[A-Z][A-Za-z]{1,15}\s+[\u4e00-\u9fff]{1,3})$/;
      
      // English single word names (2-15 characters, starts with capital)
      const englishSinglePattern = /^([A-Z][A-Za-z]{1,14})$/;
      
      // English multi-word names (up to 4 words, each starts with capital)
      const englishMultiPattern = /^([A-Z][A-Za-z]*(?:\s+[A-Z][A-Za-z]*){0,3})$/;
      
      // Chinese names with spaces (e.g., "李 小明", "张 三")
      const chineseSpacedPattern = /^([\u4e00-\u9fff](?:\s+[\u4e00-\u9fff]){1,3})$/;
      
      let potentialName = null;
      
      // Try patterns in priority order (most specific first)
      
      // 1. Chinese names (highest priority for Chinese leads)
      const chineseMatch = line.match(chineseNamePattern);
      if (chineseMatch) {
        potentialName = chineseMatch[1];
      }
      
      // 2. Mixed Chinese-English names
      if (!potentialName) {
        const mixedMatch = line.match(mixedNamePattern);
        if (mixedMatch) {
          potentialName = mixedMatch[1];
        }
      }
      
      // 3. Chinese names with spaces
      if (!potentialName) {
        const chineseSpacedMatch = line.match(chineseSpacedPattern);
        if (chineseSpacedMatch) {
          potentialName = chineseSpacedMatch[1];
        }
      }
      
      // 4. English single word names
      if (!potentialName) {
        const englishSingleMatch = line.match(englishSinglePattern);
        if (englishSingleMatch) {
          potentialName = englishSingleMatch[1];
        }
      }
      
      // 5. English multi-word names
      if (!potentialName) {
        const englishMultiMatch = line.match(englishMultiPattern);
        if (englishMultiMatch) {
          potentialName = englishMultiMatch[1];
        }
      }
      
      if (potentialName) {
        // Exclude common non-name words that might match the pattern (English only)
        const excludeWordsEnglish = [
          'Hi', 'Hello', 'Thanks', 'Best', 'Regards', 'Sincerely', 'Looking', 'Working',
          'Can', 'Let', 'We', 'Our', 'The', 'This', 'That', 'And', 'But', 'For', 'With',
          'Great', 'Good', 'Nice', 'Sure', 'Yes', 'No', 'Maybe', 'Please', 'Thank',
          'Email', 'Call', 'Meet', 'Chat', 'Talk', 'Ask', 'Tell'
        ];
        
        // Chinese common words to exclude (basic ones)
        const excludeWordsChinese = [
          '你好', '谢谢', '请问', '可以', '这是', '那是', '如果', '因为', '所以', '但是',
          '我们', '他们', '她们', '什么', '怎么', '为什么', '哪里', '什么时候'
        ];
        
        // Skip exclusion check for Chinese names (they're less likely to be common words in this context)
        const hasChinese = /[\u4e00-\u9fff]/.test(potentialName);
        
        if (hasChinese) {
          // For Chinese names, only exclude if it's a very common phrase
          if (!excludeWordsChinese.includes(potentialName) && potentialName.length >= 2) {
            return potentialName;
          }
        } else {
          // For English names, use standard exclusion
          if (!excludeWordsEnglish.includes(potentialName) && potentialName.length >= 2) {
            return potentialName;
          }
        }
      }
      
      return null;
    };
    
    // Helper function to format timestamp
    const formatTimestamp = (dateStr: string, timeStr: string): string => {
      if (!dateStr && !timeStr) return '';
      
      try {
        // Clean up date string (remove // prefix if present)
        const cleanDateStr = dateStr.replace(/^\/\/\s*/, '');
        
        // Try to parse the date and time
        if (cleanDateStr && timeStr) {
          // Example: "Wed Jan 22 2025" + "08:25 pm"
          const dateTimeStr = `${cleanDateStr} ${timeStr}`;
          const date = new Date(dateTimeStr);
          if (!isNaN(date.getTime())) {
            return date.toISOString();
          }
        }
        
        // Fallback to just date if time parsing fails
        if (cleanDateStr) {
          const date = new Date(cleanDateStr);
          if (!isNaN(date.getTime())) {
            return date.toISOString();
          }
        }
        
        // Return combined string if parsing fails
        return `${timeStr || ''} ${dateStr || ''}`.trim();
      } catch (error) {
        console.warn('Timestamp parsing error:', error);
        return `${timeStr || ''} ${dateStr || ''}`.trim();
      }
    };
    
    // Helper function to determine message sender from content with advanced confidence scoring
    const analyzeSender = (blockLines: string[]): { 
      senderName: string, 
      isUserMessage: boolean, 
      cleanContent: string,
      confidence: number,
      detectionMethod: string 
    } => {
      let senderName = 'Lead'; // Default fallback
      let isUserMessage = false;
      let senderLineIndex = -1;
      let confidence = 20; // Default low confidence
      let detectionMethod = 'fallback';
      
      const content = blockLines.join('\n');
      
      // Get active team members and their aliases for dynamic matching
      const activeTeamMembers = outreachTeamMembers.filter(member => member.is_active);
      const allTeamAliases = activeTeamMembers.flatMap(member => 
        member.aliases?.map(alias => ({
          name: alias.alias_name,
          teamMemberName: member.team_member_name,
          confidence: alias.confidence_weight || 1.0,
          isPrimary: alias.is_primary
        })) || [{ name: member.team_member_name, teamMemberName: member.team_member_name, confidence: 1.0, isPrimary: true }]
      );
      
      // Sort aliases by confidence (highest first)
      allTeamAliases.sort((a, b) => b.confidence - a.confidence);
      
      // Set default to first active team member if available
      if (activeTeamMembers.length > 0) {
        senderName = activeTeamMembers[0].team_member_name;
      }
      
      // First, look for explicit names in the content with confidence scoring
      for (let i = 0; i < blockLines.length; i++) {
        const potentialName = detectSenderName(blockLines[i]);
        if (potentialName) {
          // Check if this matches any team member alias
          const matchingAlias = allTeamAliases.find(alias => 
            alias.name.toLowerCase() === potentialName.toLowerCase()
          );
          
          if (matchingAlias) {
            // This is a team member (lead) - high confidence for exact alias match
            senderName = matchingAlias.teamMemberName;
            isUserMessage = false;
            senderLineIndex = i;
            
            // Calculate confidence based on alias confidence and position
            confidence = Math.min(95, 
              (matchingAlias.confidence * 100) + // Base alias confidence
              (i === 0 ? 15 : 5) + // Position bonus (first line gets more)
              (matchingAlias.isPrimary ? 10 : 0) // Primary alias bonus
            );
            detectionMethod = `explicit_alias_match_line_${i}`;
            break;
          } else {
            // This is likely a user/prospect response - medium confidence
            senderName = potentialName;
            isUserMessage = true;
            senderLineIndex = i;
            confidence = Math.min(85, 
              70 + // Base confidence for explicit name
              (i === 0 ? 10 : 0) + // Position bonus
              (/[\u4e00-\u9fff]/.test(potentialName) ? 5 : 0) // Chinese name bonus (less ambiguous)
            );
            detectionMethod = `explicit_name_line_${i}`;
            break;
          }
        }
      }
      
      // If no explicit name found at the start, look for signatures
      if (senderLineIndex === -1) {
        // Look for signature patterns - enhanced for multi-language support
        // Examples: "Thanks, Phoebe", "谢谢, 王明", "Best, Zhang Wei", "祝好, 李小明"
        const signaturePatterns = [
          // English signatures with Chinese/English names
          /(?:Thanks|Cheers|Best|Regards|Sincerely),?\s*\n?([\u4e00-\u9fff]{2,4}|[A-Z][A-Za-z]*(?:\s+[A-Z][A-Za-z]*)*|[\u4e00-\u9fff]{1,3}\s+[A-Z][A-Za-z]{1,15}|[A-Z][A-Za-z]{1,15}\s+[\u4e00-\u9fff]{1,3})/i,
          
          // Chinese signatures with Chinese/English names  
          /(?:谢谢|祝好|此致|敬礼|祝安|问候),?\s*\n?([\u4e00-\u9fff]{2,4}|[A-Z][A-Za-z]*(?:\s+[A-Z][A-Za-z]*)*|[\u4e00-\u9fff]{1,3}\s+[A-Z][A-Za-z]{1,15}|[A-Z][A-Za-z]{1,15}\s+[\u4e00-\u9fff]{1,3})/i,
          
          // Name at end of message (multi-language)
          /\n([\u4e00-\u9fff]{2,4}|[A-Z][A-Za-z]*(?:\s+[A-Z][A-Za-z]*)*|[\u4e00-\u9fff]{1,3}\s+[A-Z][A-Za-z]{1,15}|[A-Z][A-Za-z]{1,15}\s+[\u4e00-\u9fff]{1,3})\s*$/,
          
          // Chinese spaced names at end
          /\n([\u4e00-\u9fff](?:\s+[\u4e00-\u9fff]){1,3})\s*$/
        ];
        
        for (let patternIndex = 0; patternIndex < signaturePatterns.length; patternIndex++) {
          const pattern = signaturePatterns[patternIndex];
          const match = content.match(pattern);
          if (match && match[1]) {
            const sigName = match[1];
            
            // Check if signature matches any team member alias
            const matchingAlias = allTeamAliases.find(alias => 
              alias.name.toLowerCase() === sigName.toLowerCase()
            );
            
            if (matchingAlias) {
              // Team member signature found
              senderName = matchingAlias.teamMemberName;
              isUserMessage = false;
              confidence = Math.min(90, 
                (matchingAlias.confidence * 80) + // Base alias confidence (scaled to 80%)
                (patternIndex < 2 ? 10 : 5) + // Pattern type bonus (formal signatures get more)
                (matchingAlias.isPrimary ? 5 : 0) // Primary alias bonus
              );
              detectionMethod = `signature_alias_match_pattern_${patternIndex}`;
            } else {
              // User signature found
              senderName = sigName;
              isUserMessage = true;
              confidence = Math.min(80, 
                60 + // Base confidence for signature detection
                (patternIndex < 2 ? 10 : 5) + // Pattern type bonus
                (/[\u4e00-\u9fff]/.test(sigName) ? 5 : 0) // Chinese name bonus
              );
              detectionMethod = `signature_name_pattern_${patternIndex}`;
            }
            break;
          }
        }
      }
      
      // Special case: greetings that indicate lead messages (multi-language)
      const greetingPatterns = [
        // English greetings
        /^Hi\s+([\u4e00-\u9fff]{2,4}|[A-Z][a-z]+)/,
        /^Hello\s+([\u4e00-\u9fff]{2,4}|[A-Z][a-z]+)/,
        // Chinese greetings 
        /^你好\s*([\u4e00-\u9fff]{2,4}|[A-Z][a-z]+)/,
        /^您好\s*([\u4e00-\u9fff]{2,4}|[A-Z][a-z]+)/,
      ];
      
      for (let greetingIndex = 0; greetingIndex < greetingPatterns.length; greetingIndex++) {
        const greetingPattern = greetingPatterns[greetingIndex];
        const greetingMatch = content.match(greetingPattern);
        if (greetingMatch && senderLineIndex === -1) {
          // This is likely a lead message greeting the user
          // Use first active team member as default
          if (activeTeamMembers.length > 0) {
            senderName = activeTeamMembers[0].team_member_name;
          } else {
            senderName = 'Lead';
          }
          isUserMessage = false;
          confidence = Math.min(75, 
            50 + // Base confidence for greeting detection
            (greetingIndex < 2 ? 10 : 5) + // English vs Chinese greeting bonus
            (greetingMatch[1] ? 10 : 0) + // Named greeting bonus
            (activeTeamMembers.length > 0 ? 5 : 0) // Team member available bonus
          );
          detectionMethod = `greeting_pattern_${greetingIndex}`;
          break;
        }
      }
      
      // Enhanced lead name detection from common patterns (multi-language)
      if (senderLineIndex === -1) {
        // Check for other lead indicators in content
        const leadIndicators = [
          // English business patterns
          /I'm\s+([\u4e00-\u9fff]{2,4}|[A-Z][a-z]+),?\s+BD/i, // "I'm Lilly, BD" or "I'm 王明, BD"
          /We\s+bringing|We\s+help|We've\s+worked/i, // Business language
          /Our\s+new|Our\s+AI|Let's\s+chat/i, // Product pitches
          
          // Chinese business patterns
          /我是\s*([\u4e00-\u9fff]{2,4}|[A-Z][a-z]+)/i, // "我是王明" or "我是John"
          /我们\s*提供|我们\s*帮助|我们\s*专注/i, // Chinese business language
          /我们的\s*产品|我们的\s*服务|我们的\s*解决方案/i, // Product/service mentions
          /很高兴\s*认识|很高兴\s*联系|期待\s*合作/i, // Polite business phrases
        ];
        
        for (let indicatorIndex = 0; indicatorIndex < leadIndicators.length; indicatorIndex++) {
          const indicator = leadIndicators[indicatorIndex];
          const match = content.match(indicator);
          if (match) {
            if (match[1]) {
              // Found a name in "I'm [Name]" pattern - check if it's a team member
              const matchingAlias = allTeamAliases.find(alias => 
                alias.name.toLowerCase() === match[1].toLowerCase()
              );
              
              if (matchingAlias) {
                senderName = matchingAlias.teamMemberName;
                confidence = Math.min(85, 
                  (matchingAlias.confidence * 70) + // Base alias confidence (scaled)
                  (indicatorIndex < 2 ? 10 : 5) + // Named pattern bonus vs general business language
                  (matchingAlias.isPrimary ? 5 : 0) // Primary alias bonus
                );
                detectionMethod = `business_alias_match_indicator_${indicatorIndex}`;
              } else {
                senderName = match[1];
                confidence = Math.min(75, 
                  55 + // Base confidence for business name detection
                  (indicatorIndex < 2 ? 10 : 5) + // Named pattern bonus
                  (/[\u4e00-\u9fff]/.test(match[1]) ? 5 : 0) // Chinese name bonus
                );
                detectionMethod = `business_name_indicator_${indicatorIndex}`;
              }
            } else {
              // Found business language, use first active team member
              if (activeTeamMembers.length > 0) {
                senderName = activeTeamMembers[0].team_member_name;
              } else {
                senderName = 'Lead';
              }
              confidence = Math.min(65, 
                40 + // Base confidence for business language detection
                (indicatorIndex < 3 ? 10 : 5) + // English vs Chinese business language
                (activeTeamMembers.length > 0 ? 10 : 0) // Team member available bonus
              );
              detectionMethod = `business_language_indicator_${indicatorIndex}`;
            }
            isUserMessage = false;
            break;
          }
        }
      }
      
      // Create clean content (remove sender name line if found)
      let cleanContent = blockLines;
      if (senderLineIndex >= 0) {
        cleanContent = blockLines.filter((_, idx) => idx !== senderLineIndex);
      }
      
      const result = {
        senderName,
        isUserMessage,
        cleanContent: cleanContent.join('\n').trim(),
        confidence,
        detectionMethod
      };
      console.log('🎯 AnalyzeSender returning:', result);
      return result;
    };
    
    // Main parsing loop
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      
      if (isDateLine(line)) {
        // Save previous block if it exists
        if (currentBlock.length > 0) {
          const analyzeResult = analyzeSender(currentBlock);
          console.log('🔍 AnalyzeSender result:', analyzeResult);
          const { senderName, isUserMessage, cleanContent, confidence, detectionMethod } = analyzeResult;
          
          if (cleanContent) {
            const messageObj = {
              id: `msg-${messages.length}`,
              sender: isUserMessage ? 'user' : 'lead',
              sender_name: senderName,
              content: cleanContent,
              timestamp: formatTimestamp(currentDate, currentTime),
              confidence,
              detectionMethod
            };
            console.log('📝 Adding message with confidence:', messageObj);
            messages.push(messageObj);
          }
        }
        
        // Start new block
        currentDate = line;
        currentTime = '';
        currentBlock = [];
        
      } else if (isTimeLine(line)) {
        // This is a time line - process current block before starting new one
        if (currentBlock.length > 0) {
          const analyzeResult = analyzeSender(currentBlock);
          console.log('🔍 AnalyzeSender result:', analyzeResult);
          const { senderName, isUserMessage, cleanContent, confidence, detectionMethod } = analyzeResult;
          
          if (cleanContent) {
            const messageObj = {
              id: `msg-${messages.length}`,
              sender: isUserMessage ? 'user' : 'lead',
              sender_name: senderName,
              content: cleanContent,
              timestamp: formatTimestamp(currentDate, currentTime),
              confidence,
              detectionMethod
            };
            console.log('📝 Adding message with confidence:', messageObj);
            messages.push(messageObj);
          }
          currentBlock = [];
        }
        currentTime = line;
        
      } else {
        // Check if this line looks like a sender name (and we already have content)
        const potentialName = detectSenderName(line);
        if (potentialName && currentBlock.length > 0 && currentTime) {
          // Process the current block first
          const analyzeResult = analyzeSender(currentBlock);
          console.log('🔍 AnalyzeSender result:', analyzeResult);
          const { senderName, isUserMessage, cleanContent, confidence, detectionMethod } = analyzeResult;
          
          if (cleanContent) {
            const messageObj = {
              id: `msg-${messages.length}`,
              sender: isUserMessage ? 'user' : 'lead',
              sender_name: senderName,
              content: cleanContent,
              timestamp: formatTimestamp(currentDate, currentTime),
              confidence,
              detectionMethod
            };
            console.log('📝 Adding message with confidence:', messageObj);
            messages.push(messageObj);
          }
          
          // Start a new block with this sender name
          currentBlock = [line];
        } else {
          // This is regular content - add to current block
          currentBlock.push(line);
        }
      }
    }
    
    // Handle the last block
    if (currentBlock.length > 0) {
      const { senderName, isUserMessage, cleanContent, confidence, detectionMethod } = analyzeSender(currentBlock);
      
      if (cleanContent) {
        messages.push({
          id: `msg-${messages.length}`,
          sender: isUserMessage ? 'user' : 'lead',
          sender_name: senderName,
          content: cleanContent,
          timestamp: formatTimestamp(currentDate, currentTime),
          confidence,
          detectionMethod
        });
      }
    }
    
    console.log('🚀 EPOCH 21 PHASE 2.3 - FINAL PARSED MESSAGES:', messages);
    messages.forEach((msg, idx) => {
      console.log(`📊 Message ${idx + 1}:`, {
        sender: msg.sender_name,
        confidence: msg.confidence,
        method: msg.detectionMethod,
        content_preview: msg.content.substring(0, 50) + '...'
      });
    });
    console.log('🎯 Team members loaded:', outreachTeamMembers.length, 'members');
    return messages;
  };

  const renderConversationMessages = (messages: ConversationMessage[]) => {
    // If no parsed messages, try to parse the raw conversation
    let messagesToRender = messages;
    if (!messages || messages.length === 0) {
      if (leadResponse?.conversation_raw) {
        console.log('Raw conversation:', leadResponse.conversation_raw);
        messagesToRender = parseRawConversation(leadResponse.conversation_raw);
        console.log('Parsed messages:', messagesToRender);
      }
      
      if (!messagesToRender || messagesToRender.length === 0) {
        return (
          <div className="text-gray-500 text-sm italic">
            No parsed conversation available. Raw conversation is shown below.
          </div>
        );
      }
    }

    // Convert AI-parsed messages to frontend format
    messagesToRender = messagesToRender.map(msg => {
      // Handle AI format: sender_type: "lead" | "outreach_team"
      if (msg.sender_type) {
        return {
          ...msg,
          sender: msg.sender_type === 'lead' ? 'lead' : 'user',
          // Fix: Use lead_name for lead messages, sender for outreach messages
          sender_name: msg.sender_type === 'lead' 
            ? leadResponse?.lead_name || msg.sender 
            : msg.sender || leadResponse?.alias_profile || 'Outreach Team'
        };
      }
      return msg;
    });

    // Always try to re-parse if we have raw conversation and not many parsed messages
    if (leadResponse?.conversation_raw && messagesToRender.length <= 1) {
      console.log('Re-parsing conversation because only', messagesToRender.length, 'message(s) found');
      messagesToRender = parseRawConversation(leadResponse.conversation_raw);
      console.log('Re-parsed messages:', messagesToRender);
    }

    return (
      <div className="space-y-1">
        {messagesToRender.map((message, index) => {
          // Determine sender type for the shared component
          const isLead = message.sender === 'lead' || message.sender_type === 'lead';
          const senderType = isLead ? 'lead' : 'outreach_team';
          
          // Get sender name with fallback
          const getSenderName = () => {
            if (message.sender_name && message.sender_name.trim()) {
              return message.sender_name;
            }
            if (isLead) {
              return leadResponse?.lead_name || 'Lead';
            }
            return leadResponse?.alias_profile || 'Outreach Team';
          };

          // Display each message separately - no grouping
          
          return (
            <div key={message.id || index} className="mb-3 pb-3 border-b border-gray-100 last:border-0">
              <div className="flex items-start gap-2">
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold ${
                  senderType === 'lead' ? 'bg-green-500 text-white' : 'bg-blue-500 text-white'
                }`}>
                  {senderType === 'lead' ? 'L' : 'U'}
                </div>
                <div className="flex-1">
                  <div className="flex items-baseline gap-2 mb-1">
                    <span className="font-medium text-xs text-gray-900">
                      {getSenderName()}
                    </span>
                    {message.timestamp && (
                      <span className="text-xs text-gray-500">
                        {new Date(message.timestamp).toLocaleString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                          hour: 'numeric',
                          minute: '2-digit',
                          hour12: true
                        })}
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-gray-700 whitespace-pre-wrap">
                    {message.content || message.message}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // Removed getStatusBadge - using StatusBadge component from Epoch 34

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const canRequestReply = () => {
    if (!leadResponse || !user) return false;
    
    // Check if there's already a pending reply request
    const hasPendingRequest = leadResponse.reply_requests?.some(request => !request.sent_at);
    if (hasPendingRequest) return false;

    // Check user permissions
    const actions = getLeadResponseActions(user.user_metadata?.role || 'user');
    return actions.canRequestReply;
  };

  // Check if user can create lead responses (CSRs and admins)
  const canCreateLeadResponse = ['csr', 'admin', 'super_admin'].includes(user?.user_metadata?.role || 'user');

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-3/4 mb-4"></div>
          <div className="space-y-3">
            <div className="h-4 bg-gray-200 rounded"></div>
            <div className="h-4 bg-gray-200 rounded w-5/6"></div>
            <div className="h-4 bg-gray-200 rounded w-4/6"></div>
          </div>
          <div className="mt-6 space-y-4">
            <div className="h-32 bg-gray-200 rounded"></div>
            <div className="h-32 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !leadResponse) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="text-center text-red-600">
          <p className="font-medium">Error loading lead response</p>
          <p className="text-sm mt-1">{error || 'Lead response not found'}</p>
          <div className="flex justify-center space-x-3 mt-4">
            <button
              onClick={loadLeadResponse}
              className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
            >
              Try Again
            </button>
            {onClose && (
              <button
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Close
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Main Content */}
      <div className="bg-white rounded-lg shadow-md">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex justify-between items-start">
          <div>
            <div className="flex items-center space-x-3">
              <h1 className="text-2xl font-bold text-gray-900">{leadResponse.lead_name}</h1>
              <StatusBadge status={leadResponse.status} />
            </div>
          </div>
          <div className="flex flex-col gap-3">
            {/* Epoch 29: Add Follow-up Button - CSRs add follow-ups using navigation */}
            {console.log('🔍 Button check - isCSR:', isCSR, 'messagingConfig:', messagingConfig)}
            {isCSR && messagingConfig && (
              <button
                onClick={handleAddFollowup}
                disabled={leadResponse.conversation_closed}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add Follow-up Response
                {leadResponse.message_count && messagingConfig.max_message_exchanges && (
                  <span className="text-xs">
                    ({leadResponse.message_count || 2}/{(messagingConfig.max_message_exchanges || 5) * 2})
                  </span>
                )}
              </button>
            )}
            <div className="flex items-center space-x-3">
              <a
                href={leadResponse.lead_linkedin_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-3 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 transition-colors"
              >
                <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                </svg>
                View LinkedIn Profile
              </a>
              {onClose && (
                <button
                  onClick={onClose}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
          </div>
        </div>
        </div>

        {/* Content */}
        <div className="px-6 py-6 space-y-6">
        {/* Epoch 34: Ownership Information */}
        <OwnershipInfo 
          leadResponse={leadResponse} 
          userRole={user?.user_metadata?.role || 'user'} 
        />

        {/* Epoch 34: Lead Profile with dynamic fields */}
        {/* Epoch 76: Resolve alias LinkedIn URL from outreach team members */}
        <LeadProfile
          leadResponse={leadResponse}
          aliasLinkedinUrl={
            leadResponse.alias_profile
              ? (outreachTeamMembers.find(m =>
                  m.team_member_name.toLowerCase() === leadResponse.alias_profile!.toLowerCase() ||
                  m.aliases?.some(a => a.alias_name.toLowerCase() === leadResponse.alias_profile!.toLowerCase())
                )?.linkedin_url ?? undefined)
              : undefined
          }
        />

        {/* Pipeline Stage & Analytics */}
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700 whitespace-nowrap">Stage:</label>
              <select
                value={leadResponse.pipeline_stage || 'Lead Responded'}
                onChange={(e) => handleDispositionChange(e.target.value)}
                disabled={dispositionSaving}
                className="px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
              >
                <option value="Lead Responded">Lead Responded</option>
                <option value="1st Mtg Scheduled">1st Mtg Scheduled</option>
                <option value="1st Mtg Taken">1st Mtg Taken</option>
                <option value="Evaluating">Evaluating</option>
                <option value="Proposal Made">Proposal Made</option>
                <option value="Negotiating">Negotiating</option>
                <option value="Nurturing">Nurturing</option>
                <option value="Unqualified">Unqualified</option>
                <option value="Closed Won">Closed Won</option>
                <option value="Closed Lost">Closed Lost</option>
              </select>
              {dispositionSaving && <span className="text-xs text-gray-500">Saving...</span>}
            </div>
            {leadResponse.pipeline_stage && (
              <span className={`px-2.5 py-1 text-xs font-medium rounded-full ${
                {
                  'Lead Responded':    'bg-gray-100 text-gray-700',
                  '1st Mtg Scheduled':'bg-blue-100 text-blue-800',
                  '1st Mtg Taken':    'bg-blue-200 text-blue-900',
                  'Evaluating':       'bg-amber-100 text-amber-800',
                  'Proposal Made':    'bg-orange-100 text-orange-800',
                  'Negotiating':      'bg-purple-100 text-purple-800',
                  'Nurturing':        'bg-teal-100 text-teal-800',
                  'Unqualified':      'bg-red-100 text-red-700',
                  'Closed Won':       'bg-green-100 text-green-800',
                  'Closed Lost':      'bg-red-200 text-red-900',
                }[leadResponse.pipeline_stage] ?? 'bg-gray-100 text-gray-700'
              }`}>
                {leadResponse.pipeline_stage}
              </span>
            )}
            {/* Analytics info badges */}
            {leadResponse.is_first_response && (
              <span className="px-2.5 py-1 text-xs font-medium rounded-full bg-purple-100 text-purple-800">
                First Response
              </span>
            )}
            {leadResponse.sequence_message_number && (
              <span className="px-2.5 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                After Message #{leadResponse.sequence_message_number}
              </span>
            )}
            {leadResponse.response_trigger && leadResponse.response_trigger !== 'drip_sequence' && (
              <span className="px-2.5 py-1 text-xs font-medium rounded-full bg-teal-100 text-teal-800">
                {leadResponse.response_trigger === 'reply_request' ? 'Reply Triggered' : 'Sequence Continuation'}
              </span>
            )}
          </div>
        </div>

        {/* CSR Notes */}
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-3">
            {isCSR ? 'Your Notes (as CSR)' : 'CSR Notes'}
          </h3>
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-gray-700 whitespace-pre-wrap">
              {leadResponse.csr_notes || 'No notes available'}
            </p>
          </div>
        </div>

        {/* Conversation */}
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-3">Conversation</h3>
          
          {/* Parsed Conversation */}
          {leadResponse.conversation_parsed && leadResponse.conversation_parsed.length > 0 && (
            <div className="mb-6">
              <h4 className="text-sm font-medium text-gray-700 mb-3">Parsed Messages</h4>
              <div className="bg-gray-50 rounded-lg p-4">
                {renderConversationMessages(leadResponse.conversation_parsed)}
              </div>
            </div>
          )}

          {/* Raw Conversation - Only show for CSR users per Bug #002 */}
          {isCSR && leadResponse.conversation_raw && (
            <div className="mb-6">
              <h4 className="text-sm font-medium text-gray-700 mb-3">Raw Conversation</h4>
              <div className="bg-gray-50 rounded-lg p-4">
                <pre className="text-sm text-gray-900 whitespace-pre-wrap font-mono">
                  {leadResponse.conversation_raw}
                </pre>
              </div>
            </div>
          )}
        </div>

        {/* Reply Requests */}
        {leadResponse.reply_requests && leadResponse.reply_requests.length > 0 && (
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-3">Reply Requests</h3>
            <div className="space-y-4">
              {leadResponse.reply_requests.map((request: ReplyRequest) => (
                <div key={request.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <div className="flex items-center space-x-2">
                        <span className="font-medium text-gray-900">
                          {request.user?.full_name || 'User'}
                        </span>
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          request.sent_at
                            ? 'bg-green-100 text-green-800'
                            : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {request.sent_at ? 'Sent' : 'Pending'}
                        </span>
                        {request.is_sequence_continuation && (
                          <span className="px-2 py-1 text-xs rounded-full bg-teal-100 text-teal-800">
                            Sequence Continuation
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-gray-500">
                        Requested: {formatDate(request.created_at)}
                        {request.sent_at && (
                          <span> • Sent: {formatDate(request.sent_at)}</span>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className="space-y-3">
                    <div>
                      <span className="text-sm font-medium text-gray-700">Requested Reply:</span>
                      <p className="mt-1 text-sm text-gray-900 whitespace-pre-wrap">{request.reply_content}</p>
                    </div>
                    
                    {request.user_notes && (
                      <div>
                        <span className="text-sm font-medium text-gray-700">User Notes:</span>
                        <p className="mt-1 text-sm text-gray-600 whitespace-pre-wrap">{request.user_notes}</p>
                      </div>
                    )}
                    
                    {request.actual_message_sent && (
                      <div>
                        <span className="text-sm font-medium text-gray-700">Actual Message Sent:</span>
                        <p className="mt-1 text-sm text-gray-900 whitespace-pre-wrap">{request.actual_message_sent}</p>
                      </div>
                    )}
                    
                    {request.csr_notes && (
                      <div>
                        <span className="text-sm font-medium text-gray-700">CSR Notes:</span>
                        <p className="mt-1 text-sm text-gray-600 whitespace-pre-wrap">{request.csr_notes}</p>
                      </div>
                    )}
                    
                    {/* Mark as Sent button for CSRs */}
                    {!request.sent_at && canCreateLeadResponse && (
                      <div className="mt-4 flex justify-end">
                        <button
                          onClick={() => handleMarkReplySent(request.id)}
                          className="px-4 py-2 bg-green-600 text-white text-sm rounded-md hover:bg-green-700 transition-colors"
                        >
                          Mark Reply as Sent
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Reply Request Actions */}
        {canRequestReply() && (
          <div>
            {sequenceContinuationSuccess && (
              <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-800 text-sm">
                Sequence continuation requested successfully. Your CSR will send the next message in the sequence.
              </div>
            )}
            {!showReplyForm ? (
              <div className="flex gap-3">
                <button
                  onClick={() => setShowReplyForm(true)}
                  className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                >
                  Request Reply
                </button>
                <button
                  onClick={handleSequenceContinuation}
                  disabled={sequenceContinuationLoading || sequenceContinuationSuccess}
                  className="flex-1 px-4 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {sequenceContinuationLoading ? 'Requesting...' : 'Continue Message Sequence'}
                </button>
              </div>
            ) : (
              <div className="border border-gray-200 rounded-lg p-4">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Request Reply</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Reply Content *
                    </label>
                    <textarea
                      value={replyContent}
                      onChange={(e) => setReplyContent(e.target.value)}
                      rows={4}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="What would you like to reply to this lead?"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Additional Notes <span className="text-gray-500">(Optional)</span>
                    </label>
                    <textarea
                      value={userNotes}
                      onChange={(e) => setUserNotes(e.target.value)}
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Any additional context for the CSR..."
                    />
                  </div>
                  
                  <div className="flex justify-end space-x-3">
                    <button
                      onClick={() => {
                        setShowReplyForm(false);
                        setReplyContent('');
                        setUserNotes('');
                      }}
                      disabled={replyLoading}
                      className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleReplyRequest}
                      disabled={replyLoading || !replyContent.trim()}
                      className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {replyLoading ? 'Submitting...' : 'Submit Request'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Metadata */}
        <div className="pt-6 border-t border-gray-200">
          <div className="grid md:grid-cols-2 gap-4 text-sm text-gray-600">
            <div>
              <span className="font-medium">Created:</span> {formatDate(leadResponse.created_at)}
            </div>
            <div>
              <span className="font-medium">Updated:</span> {formatDate(leadResponse.updated_at)}
            </div>
            {leadResponse.read_by_user_at && (
              <div>
                <span className="font-medium">Read:</span> {formatDate(leadResponse.read_by_user_at)}
              </div>
            )}
            <div>
              <span className="font-medium">Alias Profile:</span> {leadResponse.alias_profile}
            </div>
          </div>
        </div>
        </div>
      </div>
      
      {/* EPOCH 29: Removed FollowupModal - using navigation to unified form instead */}
    </div>
  );
};

export default LeadResponseDetail;